// frontend/src/components/OperatorSelectionDialog.tsx
// ВЕРСИЯ: 1.1.0 (2026-09-10) — список ОД из реестра БД (useDutyOperators), резерв — OPERATORS
// УЛУЧШЕНИЯ:
// - v1.1.0: ✅ Список дежурных загружается из API /admin/settings/duty-operators (таблица duty_operators)
// - v1.1.0: ✅ Индикатор загрузки и предупреждение при работе по резервному списку
// - v1.0.1: ✅ TextField для поиска по имени или email (GPT improvement)
// - v1.0.1: ✅ Фильтрация операторов (GPT improvement)
// - v1.0.1: ✅ Explicit onClose(null) handler (GPT improvement)
// - v1.0.0: ✅ Базовый диалог с 5 операторами ГМСКЦ

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  TextField, // v1.0.1: Добавлен для поиска
} from '@mui/material';
import { OPERATORS_VERSION, Operator } from '../constants/operators.const';
import { useDutyOperators } from '../hooks/useDutyOperators';

interface OperatorSelectionDialogProps {
  open: boolean;
  onClose: (selectedOperator: Operator | null) => void;
}

const OperatorSelectionDialog: React.FC<OperatorSelectionDialogProps> = ({ open, onClose }) => {
  const [selected, setSelected] = useState<Operator | null>(null);
  const [search, setSearch] = useState(''); // v1.0.1: Поиск
  // v1.1.0: Список ОД из реестра БД; при недоступности API — резервный OPERATORS
  const { operators, loading, source } = useDutyOperators();

  // v1.0.1: Фильтрация операторов по поисковому запросу
  const filteredOperators = operators.filter(
    (op) =>
      op.fullName.toLowerCase().includes(search.toLowerCase()) ||
      op.email.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (operator: Operator) => {
    setSelected(operator);
    // Сохраняем в localStorage (с версией для автосброса при обновлении списка)
    localStorage.setItem('operator_info', JSON.stringify({ ...operator, _v: OPERATORS_VERSION }));

    // СНАЧАЛА уведомляем об изменении (до unmount!)
    window.dispatchEvent(
      new CustomEvent('operatorChanged', {
        detail: operator,
      })
    );

    // ПОТОМ закрываем диалог (может вызвать unmount)
    onClose(operator);
  };

  const handleClose = () => onClose(null); // v1.0.1: Explicit close null

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <DialogTitle>
        <Typography variant="h6">Выберите оператива ГМСКЦ</Typography>
        <Typography variant="body2" color="text.secondary">
          Это необходимо для подписи в PDF подтверждениях
        </Typography>
      </DialogTitle>
      <DialogContent>
        {/* v1.0.1: Поле поиска */}
        <TextField
          fullWidth
          variant="outlined"
          label="Поиск по имени или email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
          placeholder="Начните вводить имя или email..."
        />

        {/* v1.1.0: Состояние загрузки реестра */}
        {loading && (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
            Загрузка реестра дежурных...
          </Typography>
        )}
        {!loading && source === 'fallback' && (
          <Typography variant="body2" color="warning.main" align="center" sx={{ pb: 1 }}>
            Реестр дежурных недоступен — показан резервный список
          </Typography>
        )}

        <List>
          {filteredOperators.map((operator) => (
            <ListItem key={operator.id} disablePadding>
              <ListItemButton
                selected={selected?.id === operator.id}
                onClick={() => handleSelect(operator)}
              >
                <ListItemText primary={operator.fullName} secondary={operator.email} />
              </ListItemButton>
            </ListItem>
          ))}

          {/* v1.0.1: Сообщение если ничего не найдено */}
          {!loading && filteredOperators.length === 0 && (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
              Нет результатов по запросу "{search}"
            </Typography>
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
};

export default OperatorSelectionDialog;
